import { useCallback, useEffect, useRef, useState } from "react";

import Spinner from "../Spinner";
import { t } from "../../i18n/language";

/**
 * A real Brightcove player running the options currently being edited.
 *
 * Saving options publishes immediately — Brightcove players have no draft
 * state — so the preview is the only chance to see a change before viewers do.
 *
 * ## Why the player is never given the options directly
 *
 * The obvious approach, `bc(element, {plugins: {equalplay: pending}})`, does
 * not work and cannot be made to work. video.js initialises anything passed in
 * `options.plugins` *before* `initChildren()` (see the Player constructor in
 * video.js/dist/video.cjs.js), so `player.controlBar` does not exist yet, and
 * the Equal Play plugin builds a ZoomHandler during init which reads
 * `player.controlBar.el_`. The result is "Cannot read properties of undefined
 * (reading 'el_')" and no player at all. Brightcove's own path avoids this by
 * initialising plugins from the published configuration after the player is
 * built.
 *
 * ## What it does instead
 *
 * The player is created normally, with its published configuration, and the
 * pending options are then written into the live `player.equalplay.options`
 * object. How they take effect depends on when the plugin reads each one:
 *
 *   * `signBgColor` and `signSize` are read at the point of use, so they are
 *     applied to the running player exactly the way the plugin's own settings
 *     panel does it (see `settingsEvent` in the plugin's signLanguage.js).
 *     `signPos` only seeds the interpreter when its canvas is created, but the
 *     canvas can be moved afterwards, so it is applied live too - and dragging
 *     the interpreter reports back through the plugin's `sign_resize` event,
 *     which is how the position fields stay in step with the player.
 *   * Everything else is read while the controls are built — which happens on
 *     `loadstart`, not at plugin init. Re-loading the video through the
 *     Brightcove catalog re-runs `clearControls` and rebuilds every control
 *     from the current options, so these need a reload but not a new player.
 *   * `useZoomHandler` is the one exception: the ZoomHandler is constructed
 *     once during plugin init and never again, so changing it means disposing
 *     the player and building a new one.
 *
 * The element is mounted only after the embed script has loaded, which keeps
 * Brightcove's auto-initialisation from claiming it first, and it is created
 * without `data-video-id` so the first video load happens after the pending
 * options are in place — otherwise the first build of the controls would use
 * the saved options.
 */

/** Applied straight to the running player, with no reload. */
const LIVE_OPTIONS = ["signBgColor", "signSize", "signPos"];

/** Read once during plugin init: needs a whole new player. */
const REBUILD_OPTIONS = ["useZoomHandler"];

/** The plugin's canvas for the interpreter, when one is on screen. */
const signCanvas = (player) => {
  const canvas = player?.equalplay?.signLanguage?.signLanguageCanvas;
  if (!canvas || canvas.isDisposed_) return null;
  return canvas.getCanvas ? canvas.getCanvas() : null;
};

function PlayerPreview({ accountId, playerId, videoId, options, onSignPosition }) {
  const holderRef = useRef(null);
  const playerRef = useRef(null);
  const appliedRef = useRef(null);
  // The video the player currently holds. Tracked separately from the options
  // because picking a different video changes nothing about them, and the
  // options diff below would otherwise find no work to do and never load it.
  const loadedVideoRef = useRef(null);
  // The embed <script> this component added, so it can be replaced rather than
  // accumulated. See the script effect for why re-adding matters.
  const scriptRef = useRef(null);
  const [scriptReady, setScriptReady] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  // Fatal: the script or the player itself failed, so there is nothing to show.
  const [error, setError] = useState(null);
  // Non-fatal: the player is fine but this video would not load. Kept separate
  // so a bad video does not hide a working player — which left the preview
  // stuck until you switched players.
  const [videoError, setVideoError] = useState(null);
  // Bumped only for the options that need a brand new player.
  const [buildCount, setBuildCount] = useState(0);

  /** Dispose the player and empty its holder. Safe to call repeatedly. */
  const teardown = useCallback(() => {
    if (playerRef.current) {
      try {
        playerRef.current.dispose();
      } catch (err) {
        console.warn("Could not dispose preview player", err);
      }
      playerRef.current = null;
    }
    if (holderRef.current) holderRef.current.innerHTML = "";
    appliedRef.current = null;
    loadedVideoRef.current = null;
  }, []);

  // One embed script per player. Switching players loads another one; they
  // coexist, and `bc()` picks the definition from the element's data-player.
  useEffect(() => {
    if (!accountId || !playerId) return undefined;

    setScriptReady(false);
    setPlayerReady(false);

    // Tear the old player down before the next bundle arrives. Every
    // Brightcove embed script initialises any <video-js> it finds in the
    // document when it runs, so an element left over from the previous player
    // gets claimed by the incoming bundle: our own bc() call then reports
    // "Ignoring already initialized player", and what is on screen is a
    // half-built player belonging to neither — which is where the null
    // `tagName` and text-track errors come from.
    teardown();

    // A fresh script element every time, even for a player whose bundle has
    // run before.
    //
    // Each Brightcove bundle ends by overwriting `window.bc` with an instance
    // built around its OWN account and player, both baked in at build time -
    // it does not read the element's data-account. So `window.bc` is simply
    // whichever bundle executed last. Re-using an existing <script> tag for a
    // player visited earlier in the session leaves `window.bc` pointing at the
    // player visited since, and that bundle then initialises our element with
    // the wrong account: the catalog asks for this account's video ids under
    // the other account's id and Brightcove answers 404. Appending the tag
    // again re-executes the bundle and puts `window.bc` back where it belongs.
    const previous = scriptRef.current;
    if (previous && previous.parentNode) {
      previous.parentNode.removeChild(previous);
    }

    const script = document.createElement("script");
    script.src = `https://players.brightcove.net/${accountId}/${playerId}_default/index.min.js`;
    script.async = true;
    script.onload = () => setScriptReady(true);
    script.onerror = () =>
      setError(t("Could not load this player from Brightcove"));

    scriptRef.current = script;
    document.body.appendChild(script);

    return undefined;
  }, [accountId, playerId, teardown]);

  // Build the player. No video and no options — both are applied below, in
  // that order, so the controls are built from the pending options the first
  // time rather than from what is saved.
  useEffect(() => {
    if (!scriptReady || !accountId || !playerId) return undefined;

    const holder = holderRef.current;
    if (!holder || !window.bc) return undefined;

    setPlayerReady(false);
    setVideoError(null);
    teardown();

    const element = document.createElement("video-js");
    element.setAttribute("data-account", accountId);
    element.setAttribute("data-player", playerId);
    element.setAttribute("data-embed", "default");
    element.setAttribute("data-application-id", "Equal Play");
    element.setAttribute("controls", "");
    element.className = "vjs-fluid";
    holder.appendChild(element);

    try {
      const player = window.bc(element);
      playerRef.current = player;

      player.ready(() => {
        if (!player.equalplay) {
          setError(t("This player does not have Equal Play installed"));
          return;
        }
        setError(null);
        setPlayerReady(true);
      });
    } catch (err) {
      console.error("Could not start preview player", err);
      setError(err.message || t("Could not start the preview player"));
    }

    return undefined;
  }, [scriptReady, accountId, playerId, buildCount, teardown]);

  // Apply the options, and load the video when they need a rebuild of the
  // controls. Debounced: dragging a slider must not reload the video on every
  // frame.
  useEffect(() => {
    if (!playerReady || !videoId) return undefined;

    const player = playerRef.current;
    if (!player || !player.equalplay) return undefined;

    const previous = appliedRef.current;
    const changedKeys = previous
      ? Object.keys({ ...previous, ...options }).filter(
          (key) => JSON.stringify(previous[key]) !== JSON.stringify(options[key])
        )
      : Object.keys(options);

    const videoChanged = loadedVideoRef.current !== videoId;

    if (changedKeys.length === 0 && !videoChanged) return undefined;

    if (previous && changedKeys.some((key) => REBUILD_OPTIONS.includes(key))) {
      setBuildCount((count) => count + 1);
      return undefined;
    }

    // A different video always goes through the load path below, whatever the
    // options did.
    const liveOnly =
      !videoChanged &&
      !!previous &&
      changedKeys.every((key) => LIVE_OPTIONS.includes(key));

    if (liveOnly) {
      appliedRef.current = options;
      const equalplay = player.equalplay;
      const canvas = signCanvas(player);

      if (changedKeys.includes("signBgColor")) {
        equalplay.options.signBgColor = options.signBgColor;
        // The plugin reads `userSignBgColor` first and falls back to
        // `signBgColor`, so setting the former is what a viewer's own choice
        // does — which is exactly what this option is: a starting value a
        // viewer can override.
        equalplay.options.userSignBgColor = options.signBgColor;
        if (canvas) canvas.style.backgroundColor = options.signBgColor;
      }

      if (changedKeys.includes("signSize")) {
        equalplay.options.signSize = options.signSize;
        // `zoom` takes the stored 20-50 scale directly; the plugin's own
        // slider shows a 1-100 expansion of the same number. It re-anchors the
        // interpreter so it grows from its centre, and reports the resulting
        // position back through `sign_resize` — which is why resizing also
        // moves the position fields.
        if (canvas && canvas.zoom) canvas.zoom(options.signSize);
      }

      if (changedKeys.includes("signPos")) {
        equalplay.options.signPos = options.signPos;
        const [left, top, width] = options.signPos;
        // Not `canvas.setPosition`: it guards each value with `if (left)`, so a
        // legitimate 0 — the interpreter flush against an edge — is dropped.
        if (canvas) {
          canvas.style.left = `${left}%`;
          canvas.style.top = `${top}%`;
          canvas.style.width = `${width}%`;
        }
      }

      return undefined;
    }

    const timer = setTimeout(() => {
      appliedRef.current = options;
      Object.assign(player.equalplay.options, options);
      // Dropped so the plugin falls back to signBgColor — otherwise a colour
      // set during an earlier live edit would outrank the one being loaded.
      delete player.equalplay.options.userSignBgColor;

      // Re-loading re-fires loadstart, which clears the Equal Play controls and
      // builds them again from the options above.
      player.catalog.getVideo(videoId, (err, video) => {
        if (err) {
          // Brightcove's Playback API answers 404 for anything it will not
          // serve — inactive, still ingesting, or restricted by the player's
          // policy. The player itself is fine, so say so beside it and leave
          // it running rather than replacing it with an error.
          console.error("Could not load the preview video", err);
          setVideoError(t("That video could not be played by this player."));
          return;
        }
        setVideoError(null);
        loadedVideoRef.current = videoId;
        player.catalog.load(video);
      });
    }, 350);

    return () => clearTimeout(timer);
  }, [playerReady, videoId, options]);

  // Dragging or resizing the interpreter in the player reports back here, so
  // the position fields follow what is on screen.
  //
  // The reported position is also written into `appliedRef` before it is
  // handed up: it comes back down as a changed `signPos`, and without this the
  // diff above would see a change and push the same values back at the canvas
  // mid-drag.
  useEffect(() => {
    if (!playerReady || !onSignPosition) return undefined;

    const player = playerRef.current;
    const equalplay = player?.equalplay;
    if (!equalplay || typeof equalplay.on !== "function") return undefined;

    const round = (n) => Math.round(n * 100) / 100;

    const handleResize = (el, data) => {
      const position = [round(data.left), round(data.top), round(data.width)];
      if (appliedRef.current) {
        appliedRef.current = { ...appliedRef.current, signPos: position };
      }
      onSignPosition(position);
    };

    equalplay.on("sign_resize", handleResize);

    return () => {
      equalplay.off("sign_resize", handleResize);
    };
  }, [playerReady, buildCount, onSignPosition]);

  // Tear the player down on unmount — without this the video keeps playing
  // after navigating away.
  useEffect(() => {
    return () => {
      teardown();
      const script = scriptRef.current;
      if (script && script.parentNode) script.parentNode.removeChild(script);
      scriptRef.current = null;
    };
  }, [teardown]);

  const showPlayer = scriptReady && !error;

  return (
    <div>
      <div className="bg-white rounded border border-gray-200 overflow-hidden">
        {!videoId && (
          <div className="flex h-40 items-center justify-center text-sm text-gray-500">
            {t("Choose a video to preview with")}
          </div>
        )}
        {videoId && !scriptReady && !error && (
          <div className="flex flex-col h-40 items-center justify-center">
            <Spinner />
            <p className="mt-2 text-sm text-gray-500">
              {t("Preparing preview player")}
            </p>
          </div>
        )}
        {error && (
          <div className="px-3 py-4 text-sm text-gray-700 bg-yellow-100">
            {error}
          </div>
        )}
        <div ref={holderRef} className={showPlayer ? "" : "hidden"} />
        {videoError && showPlayer && (
          <div className="px-3 py-2 text-sm text-gray-700 bg-yellow-100 border-t border-yellow-300">
            {videoError}
          </div>
        )}
      </div>
      <p className="mt-2 text-xs text-gray-600">
        {t("Nothing here is saved until you choose to save.")}
      </p>
    </div>
  );
}

export default PlayerPreview;
